import React, { useState } from 'react';
import { Loader2, Send, Copy, Check, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ConvertBrainDialog = ({ brain, token, onClose, onInvited }) => {
  const [email, setEmail] = useState(brain?.invite?.email || '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const send = async (sendEmail) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/demo-brains/${brain.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, origin_url: window.location.origin, send_email: sendEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please enter a valid email');
      setResult(data);
      onInvited(brain.id, { email, link: data.link, status: 'pending', expires_at: data.expires_at });
      if (sendEmail) toast[data.email_sent ? 'success' : 'warning'](data.email_sent ? `Signup link emailed to ${email}` : 'Link created, but the email could not be sent. Copy it instead.');
      else toast.success('Signup link created');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(result.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied');
  };

  return (
    <Dialog open={!!brain} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white text-slate-900 border-slate-200 sm:max-w-md grid-cols-1 [&>*]:min-w-0" data-testid="convert-brain-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900"><UserPlus className="w-5 h-5 text-blue-600" />Convert to Customer</DialogTitle>
          <DialogDescription className="text-slate-500">
            Send {brain?.business_name} a signup link. Their Pro goes live with this exact brain, voice and pacing once they pay. Link valid 14 days.
          </DialogDescription>
        </DialogHeader>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@business.com" className="bg-white border-slate-300 text-slate-900" data-testid="convert-email-input" />
        <div className="flex gap-2">
          <Button onClick={() => send(true)} disabled={sending || !email} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="convert-send-email-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Email Link
          </Button>
          <Button onClick={() => send(false)} disabled={sending || !email} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="convert-link-only-btn">
            Link Only
          </Button>
        </div>
        {result && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200 min-w-0 w-full overflow-hidden">
            <code className="flex-1 min-w-0 text-xs text-slate-700 truncate" data-testid="convert-link-text">{result.link}</code>
            <Button size="sm" onClick={copy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="convert-copy-link-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}<span className="ml-1">Copy Link</span>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
